import React from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, User, LogOut, Settings, FileText, Compass } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const { user, isAuthenticated, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  // Don't show header on landing page (has its own header)
  if (location.pathname === '/') {
    return null
  }

  const handleLogout = () => {
    logout()
    navigate('/')
    setIsMenuOpen(false)
  }

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  return (
    <header className="bg-white border-b border-secondary-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            <span className="text-xl font-bold gradient-text">Samvadic</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-secondary-600 hover:text-primary-600 font-medium transition-colors">
                  Dashboard
                </Link>
                <Link to="/journey" className="text-secondary-600 hover:text-primary-600 font-medium transition-colors">
                  Journey
                </Link>
                <Link to="/anchors" className="text-secondary-600 hover:text-primary-600 font-medium transition-colors">
                  Anchors
                </Link>
                <Link to="/consultant" className="text-secondary-600 hover:text-primary-600 font-medium transition-colors">
                  Consultant
                </Link>
                <Link to="/documents" className="text-secondary-600 hover:text-primary-600 font-medium transition-colors">
                  Documents
                </Link>
                
                {/* User Menu */}
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-secondary-600 hover:text-primary-600 transition-colors">
                    <User className="w-5 h-5" />
                    <span className="font-medium">{user?.full_name || user?.email}</span>
                  </button>
                  
                  {/* Dropdown */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-2">
                      <Link 
                        to="/settings" 
                        className="flex items-center space-x-2 px-4 py-2 text-secondary-600 hover:bg-secondary-50 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </Link>
                      <button 
                        onClick={handleLogout}
                        className="flex items-center space-x-2 w-full px-4 py-2 text-secondary-600 hover:bg-secondary-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="text-secondary-600 hover:text-primary-600 font-medium transition-colors">
                  Login
                </Link>
                <Link to="/register" className="btn-primary">
                  Get Started
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-lg text-secondary-600 hover:bg-secondary-50 transition-colors"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-secondary-200">
          <div className="px-4 py-6 space-y-4">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="flex items-center space-x-3 text-secondary-600 hover:text-primary-600 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Compass className="w-5 h-5" />
                  <span>Dashboard</span>
                </Link>
                <Link 
                  to="/journey" 
                  className="flex items-center space-x-3 text-secondary-600 hover:text-primary-600 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Compass className="w-5 h-5" />
                  <span>Journey</span>
                </Link>
                <Link 
                  to="/anchors" 
                  className="flex items-center space-x-3 text-secondary-600 hover:text-primary-600 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Compass className="w-5 h-5" />
                  <span>Anchors</span>
                </Link>
                <Link 
                  to="/consultant" 
                  className="flex items-center space-x-3 text-secondary-600 hover:text-primary-600 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User className="w-5 h-5" />
                  <span>Consultant</span>
                </Link>
                <Link 
                  to="/documents" 
                  className="flex items-center space-x-3 text-secondary-600 hover:text-primary-600 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FileText className="w-5 h-5" />
                  <span>Documents</span>
                </Link>
                
                <div className="border-t border-secondary-200 pt-4">
                  <div className="flex items-center space-x-3 text-secondary-800 font-medium mb-3">
                    <User className="w-5 h-5" />
                    <span>{user?.full_name || user?.email}</span>
                  </div>
                  <Link 
                    to="/settings"
                    className="flex items-center space-x-3 text-secondary-600 hover:text-primary-600 font-medium transition-colors mb-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Settings className="w-5 h-5" />
                    <span>Settings</span>
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center space-x-3 text-secondary-600 hover:text-primary-600 font-medium transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="block text-secondary-600 hover:text-primary-600 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="block btn-primary text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}

export default Header
